import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, FlaskConical, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, BACKEND_URL, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/layout/AppLayout";

const ERRORS = {
  "non-autorise": "Votre compte Microsoft a bien été identifié mais vous n'avez pas accès à l'Annuaire La Mêlée. Contactez un administrateur si vous pensez qu'il s'agit d'une erreur.",
  microsoft: "La connexion Microsoft n'a pas pu être validée. Utilisez votre compte professionnel La Mêlée.",
  annule: "La connexion Microsoft a été annulée.",
  expire: "La demande de connexion a expiré. Réessayez.",
  "non-configure": "La connexion Microsoft n'est pas encore configurée.",
};

const MicrosoftLogo = () => (
  <svg viewBox="0 0 21 21" className="h-5 w-5" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" /><rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" /><rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

function DemoLogin({ onDone }) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/demo-login", { email });
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="demo-login-form">
      <p className="flex items-center gap-2 text-sm font-semibold text-amber-900"><FlaskConical className="h-4 w-4" /> Connexion démo — aperçu uniquement</p>
      <p className="mt-1 text-xs text-amber-800">Désactivée automatiquement dès que Microsoft est configuré.</p>
      <div className="mt-3 flex gap-2">
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="prenom.nom@lamelee.com" className="bg-white" data-testid="demo-login-email-input" />
        <Button type="submit" disabled={busy || !email.trim()} data-testid="demo-login-submit">{busy ? "…" : "Entrer"}</Button>
      </div>
      {error && <p className="mt-2 text-sm font-medium text-red-700" data-testid="demo-login-error">{error}</p>}
    </form>
  );
}

export default function LoginPage() {
  const { isAuthed, config, refreshAll } = useAuth();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const next = sp.get("next") || "/";
  const error = ERRORS[sp.get("erreur")];
  const microsoft = config?.auth?.microsoft;

  useEffect(() => { if (isAuthed) navigate(next, { replace: true }); }, [isAuthed, next, navigate]);

  return (
    <div className="lm-container grid min-h-[calc(100vh-8rem)] items-center gap-10 py-12 lg:grid-cols-2">
      <div className="hidden rounded-[2rem] bg-[#A4247F] p-10 text-white lg:block" style={{ backgroundImage: "radial-gradient(600px 300px at 100% 0%, rgba(236,127,19,.55), transparent 60%)" }}>
        <Sparkles className="h-8 w-8 text-[#FFD7B0]" />
        <h2 className="mt-6 text-3xl font-extrabold leading-tight">L'annuaire de l'écosystème, avec les informations internes en plus.</h2>
        <ul className="mt-8 space-y-3 text-white/85">
          <li>• Coordonnées complètes des contacts</li>
          <li>• Création et modification des fiches dans Notion</li>
          <li>• Listes, copie des emails et export CSV</li>
        </ul>
      </div>
      <div className="mx-auto w-full max-w-md lm-fade-up" data-testid="login-page">
        <Logo className="h-16" />
        <h1 className="mt-8 text-3xl font-extrabold">Annuaire La Mêlée</h1>
        <p className="mt-2 text-stone-600">Connectez-vous avec votre compte professionnel La Mêlée pour accéder à l'annuaire interne.</p>
        {error && (
          <div className="mt-6 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-800" data-testid="login-error-message">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
          </div>
        )}
        <Button asChild={microsoft} disabled={!microsoft} className="mt-6 h-12 w-full gap-3 rounded-xl border border-stone-300 bg-white text-base font-semibold text-stone-900 hover:bg-stone-50" data-testid="login-microsoft-button">
          {microsoft
            ? <a href={`${BACKEND_URL}/api/auth/microsoft/login?next=${encodeURIComponent(next)}`}><MicrosoftLogo /> Se connecter avec Microsoft</a>
            : <span className="flex items-center gap-3"><MicrosoftLogo /> Se connecter avec Microsoft</span>}
        </Button>
        {!microsoft && <p className="mt-2 text-xs text-muted-foreground" data-testid="login-microsoft-pending">Connexion Microsoft en cours de configuration.</p>}
        <p className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5" /> Aucun mot de passe n'est saisi ni conservé par l'annuaire.</p>
        {config?.auth?.demo && <DemoLogin onDone={refreshAll} />}
      </div>
    </div>
  );
}
